import { Spacing } from '@persona-id/design-tokens';
import { LinkIcon } from '@persona-id/design-tokens/icons';
import { clamp } from 'lodash/fp';
import { Fragment, ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import Markdown from 'shared/components/Markdown';
import styled, { css } from 'styled-components';

import Button, { ButtonType } from 'inquiry/components/Button';
import CustomAssetWithFallback from 'inquiry/components/CustomAssetWithFallback';
import ThemedParagraph from 'inquiry/components/ThemedParagraph';
import ThemedTitle from 'inquiry/components/ThemedTitle';
import VerticalOption from 'inquiry/components/VerticalOption/VerticalOption';
import { useBaseStepStyleContext } from 'inquiry/context/BaseStepStyleContext';
import { colorCss, fontWeightCss, sizeCss } from 'lib/inquiryStyles';
import DeviceHandoffOption from 'lib/interfaces/DeviceHandoffOption';
import type {
  DeviceHandoffAssets,
  DeviceHandoffPromptPageLocalizations,
} from 'lib/interfaces/StepsConfig';
import { ButtonComponentStyles, TextComponentStyles } from 'lib/interfaces/ui-components';
import { assertNever } from 'lib/utils/types';
import CenteredPictograph from 'verify/components/CenteredPictograph';
import PositionableButtonContainer from 'verify/components/PositionableButtonContainer/PositionableButtonContainer';
import QrCode from 'verify/components/QrCode';
import { HolderClient } from 'verify/lib/clients/HolderClient';
import { fontFamilyCss } from 'verify/lib/utils/font-family';
import { MIN_HEIGHTS } from 'verify/styles/variables';

const QR_CODE_MIN_SIZE = 132;
const QR_CODE_MAX_SIZE = 256;

const Header = styled('div')`
  min-height: ${MIN_HEIGHTS.header};
  margin-bottom: ${Spacing[6]};
`;

const OptionButton = styled(Button)`
  width: 100%;
  ${({ styles }) =>
    styles == null &&
    css`
      & + & {
        margin-top: ${Spacing[4]};
      }
    `}
`;

const CopyToClipboardButton = styled(Button)<{
  styles?: ButtonComponentStyles;
  textStyles?: TextComponentStyles;
}>(({ styles, textStyles }) => ({
  backgroundColor: 'transparent !important',
  border: 0,
  borderBottom: '1px solid',
  borderRadius: 0,
  boxShadow: 'unset !important',
  margin: 0,
  padding: 0,
  textTransform: 'unset',
  transform: 'none !important',
  width: 'initial',

  borderColor: `${colorCss(styles?.['text-color'].base.base)} !important`,
  fontFamily: fontFamilyCss(textStyles?.['font-family'].base.base),
  fontSize: sizeCss(textStyles?.['font-size'].base.base),
  fontWeight: fontWeightCss(textStyles?.['font-weight'].base.base),
  letterSpacing: sizeCss(textStyles?.['letter-spacing'].base.base),
  lineHeight: sizeCss(textStyles?.['line-height'].base.base),

  svg: {
    height: sizeCss(textStyles?.['font-size'].base.base),
    width: sizeCss(textStyles?.['font-size'].base.base),
  },
}));

const QrCodeContainer = styled.div`
  height: ${QR_CODE_MIN_SIZE}px;
  margin-bottom: ${Spacing[6]};
  display: flex;
  flex-direction: column;
  align-items: center;
`;

const IconContainer = styled.span`
  line-height: 0;
  margin-right: 10px;
`;

const Container = styled.div`
  display: flex;
  flex-direction: column;
`;

interface InquiryDeviceHandoffPromptViewProps {
  assets: DeviceHandoffAssets['device-handoff-prompt-page'];
  deviceHandoffOptions: DeviceHandoffOption[];
  deviceHandoffListStyle?: 'outline' | 'outline_light' | 'button';
  handleEmailDeviceHandoff: () => void;
  handleSmsDeviceHandoff: () => void;
  link: string | undefined;
  shortlink: string | undefined;
  copyShortlink: () => void;
  localizations?: DeviceHandoffPromptPageLocalizations;
}

export default function InquiryDeviceHandoffPromptView({
  assets,
  deviceHandoffOptions,
  deviceHandoffListStyle = 'button',
  handleEmailDeviceHandoff,
  handleSmsDeviceHandoff,
  link,
  shortlink,
  copyShortlink,
  localizations,
}: InquiryDeviceHandoffPromptViewProps) {
  const [t] = useTranslation();
  const { base, buttons } = useBaseStepStyleContext();

  const DeviceHandoffSectionContainer =
    deviceHandoffListStyle === 'button' ? PositionableButtonContainer : Fragment;

  const customLink = localizations?.['qr-code'];
  const qrCodeDimensions = customLink ? getQrCodeSize(customLink.length) : QR_CODE_MIN_SIZE;
  // || instead of ?? since this defaults to ''
  const qrCodeLink = customLink || link;

  const showCopyShortlinkButton = shortlink != null && HolderClient.canCopyToClipboard;

  return (
    <Container>
      <CustomAssetWithFallback customImage={assets['header-pictograph']} alt='header pictograph' />
      <Header>
        <ThemedTitle styles={base?.titleStyle}>
          <Markdown>{localizations?.['title'] ?? t('to-mobile.title')}</Markdown>
        </ThemedTitle>
        <ThemedParagraph styles={base?.textStyle}>
          <Markdown>{localizations?.['description'] ?? t('to-mobile.description')}</Markdown>
        </ThemedParagraph>
      </Header>
      {deviceHandoffOptions.includes(DeviceHandoffOption.Qr) && (
        <QrCodeContainer style={{ height: `${qrCodeDimensions}px` }}>
          {!!qrCodeLink && (
            <>
              <CenteredPictograph
                height={qrCodeDimensions}
                width={qrCodeDimensions}
                aria-label={t('qr-code.prompt')}
                role='img'
                marginBottom={shortlink ? '0' : undefined}
              >
                <QrCode
                  value={qrCodeLink}
                  accessibleTitle={t('qr-code.prompt')}
                  data-test='device-handoff-qr__qr-code'
                  bgColor={colorCss(base?.backgroundColor)}
                  fgColor={colorCss(base?.textStyle?.['text-color']?.base?.base)}
                />
              </CenteredPictograph>
              {showCopyShortlinkButton && (
                <CopyToClipboardButton
                  onClick={copyShortlink}
                  id='device-handoff-qr__copy-shortlink'
                  suffixIcon={<LinkIcon />}
                  styles={buttons?.secondary}
                  textStyles={base?.disclaimerStyle}
                >
                  {shortlink}
                </CopyToClipboardButton>
              )}
            </>
          )}
        </QrCodeContainer>
      )}
      <DeviceHandoffSectionContainer>
        {(deviceHandoffOptions ?? []).map((option, index) => {
          return (
            <OptionBlock
              key={option}
              isPrimary={index === 0}
              option={option}
              assets={assets}
              deviceHandoffOptions={deviceHandoffOptions}
              handleEmailDeviceHandoff={handleEmailDeviceHandoff}
              handleSmsDeviceHandoff={handleSmsDeviceHandoff}
              localizations={localizations}
              deviceHandoffListStyle={deviceHandoffListStyle}
            />
          );
        })}
      </DeviceHandoffSectionContainer>
    </Container>
  );
}

interface OptionBlockProps
  extends Omit<InquiryDeviceHandoffPromptViewProps, 'link' | 'shortlink' | 'copyShortlink'> {
  isPrimary: boolean;
  option: DeviceHandoffOption;
}

function OptionBlock({
  assets,
  handleSmsDeviceHandoff,
  handleEmailDeviceHandoff,
  isPrimary,
  localizations,
  option,
  deviceHandoffListStyle,
}: OptionBlockProps) {
  const [t] = useTranslation();

  switch (option) {
    case DeviceHandoffOption.Sms:
      return (
        <Option
          id='button__sms'
          isPrimary={isPrimary}
          data-test='to-mobile__via-sms'
          onClick={handleSmsDeviceHandoff}
          icon={
            <CustomAssetWithFallback customImage={assets['icon-sms']} alt='mobile phone icon' />
          }
          deviceHandoffListStyle={deviceHandoffListStyle}
        >
          {localizations?.['via-sms'] ?? t('to-mobile.via-sms')}
        </Option>
      );
    case DeviceHandoffOption.Email:
      return (
        <Option
          id='button__email'
          isPrimary={isPrimary}
          data-test='to-mobile__via-email'
          onClick={handleEmailDeviceHandoff}
          icon={<CustomAssetWithFallback customImage={assets['icon-email']} alt='email icon' />}
          deviceHandoffListStyle={deviceHandoffListStyle}
        >
          {localizations?.['via-email'] ?? t('to-mobile.via-email')}
        </Option>
      );
    case DeviceHandoffOption.Qr:
      return null;

    default:
      assertNever(option);
  }
}

interface OptionProps {
  'data-test': string;
  children: ReactNode;
  icon: ReactNode | null;
  id: string;
  isPrimary: boolean;
  onClick: () => void;
  deviceHandoffListStyle?: 'outline' | 'outline_light' | 'button';
}

const Option = ({
  'data-test': dataTest,
  children,
  icon,
  id,
  isPrimary,
  onClick,
  deviceHandoffListStyle = 'outline',
}: OptionProps) => {
  const { buttons } = useBaseStepStyleContext();

  const childProps = {
    id,
    'data-test': dataTest,
    onClick,
  };

  const buttonType = isPrimary ? ButtonType.Primary : ButtonType.Secondary;

  if (deviceHandoffListStyle === 'button') {
    return (
      <OptionButton
        {...childProps}
        type='button'
        styles={buttons?.[buttonType]}
        buttonType={buttonType}
      >
        {children}
      </OptionButton>
    );
  }

  // TODO: consider supporting SST for this the same way Gov ID does
  return (
    <VerticalOption
      {...childProps}
      buttonType={deviceHandoffListStyle}
      icon={
        icon != null && (
          <IconContainer data-test='option-vertical__pictograph'>{icon}</IconContainer>
        )
      }
    >
      {children}
    </VerticalOption>
  );
};

/**
 * Compute reasonable dimensions for custom QR codes.
 * QR code AREA should increase linearly with URL length, so QR code side length should increase linearly with URL length ^ 0.5.
 * Persona QR codes are ~60 chars long.
 */
function getQrCodeSize(urlLength: number): number {
  return clamp(QR_CODE_MIN_SIZE, QR_CODE_MAX_SIZE, Math.floor(Math.sqrt(urlLength) * 16));
}
